/**
 * "Convert all the rates" — every allocation row restated into the company's currency.
 *
 * The comparison page shows each offer in the supplier's currency with the company-currency
 * figure beside it. Akhil (2026-09-14) asked for one click that CHANGES the rates to the
 * converted values, so the quotation is built from them and the sheet reads in one currency.
 *
 * `buildQuotationPayload` converts on its own now too, so this is not what makes the quotation
 * right — it is what makes the page say what the quotation will say. Idempotent: a row already
 * in the company's currency is returned as the same object, and running it twice changes nothing.
 *
 * The supplier's own figure is never lost: `quoted_rate` keeps it (set from `rate` if nothing
 * recorded one yet) and `quoted_currency` keeps what it was in, which is what the quotation
 * line's PO side (`po_currency`, `po_currency_rate`) is built from.
 *
 * A currency with no rate on file is reported by name and its rows left untouched — the page
 * says so, and the build refuses it later with the same reason, rather than either pricing it
 * at par.
 */

import { pickRate, type ExchangeRateRow } from './exchangeRates'
import type { AllocationRow, Currency } from './types'

export function convertAllocationsToBase(
  rows: readonly AllocationRow[],
  baseCurrency: Currency,
  rates: readonly ExchangeRateRow[],
): { rows: AllocationRow[]; converted: number; missing: string[] } {
  const base = baseCurrency.trim().toUpperCase()
  const missing = new Set<string>()
  let converted = 0

  const out = rows.map((row) => {
    const from = (row.currency || base).trim().toUpperCase()
    if (from === base) return row
    const picked = pickRate(rates, from, base)
    if (picked === null) {
      missing.add(from)
      return row
    }
    converted += 1
    return {
      ...row,
      rate: Math.round(row.rate * picked.rate * 1e6) / 1e6,
      currency: baseCurrency,
      quoted_rate: row.quoted_rate ?? row.rate,
      quoted_currency: row.quoted_currency ?? row.currency,
    }
  })

  return { rows: out, converted, missing: [...missing] }
}
